/**
 * « Réclamations reçues » on the fournisseur portal — /portail-fournisseur/reclamations and /:id
 * (design/ux/03_avoirs_bc_reclamations.md §5.1 « Fournisseur sees », §5.8, §5.9; refonte WP-36). Every
 * sentence of the list, the document, its dialogs and the thread as the SUPPLIER reads it (« vous » =
 * the supplier's org, the pharmacy by its name), verbatim from the design where it gives one, once.
 * Vouvoiement, sentence case, no raw enum value, no English. The server's own refusals (409 / 422)
 * are shown as the server words them.
 */
import { RECLAMATION_BORNES as B, VUE_PORTAIL_RECLAMATION as V } from "@pharmakon/shared/constants/reclamations.js";

const pluriel = (n, un, plusieurs) => (n > 1 ? plusieurs : un);

/** ux/03 §5.8 — the list. */
export const M_RP = Object.freeze({
  groupe: "Commerce",
  titre: "Réclamations reçues",
  sousTitre: "Les réclamations que les pharmacies vous adressent sur Pharmakon — manquants, casse, péremption, erreurs, prix — et vos réponses",
  recherche: "Rechercher : N°, pharmacie, produit …",
  grille: "Réclamations reçues",
  vuesLabel: "Vue",
  vues: Object.freeze({
    [V.A_TRAITER]: "À traiter",
    [V.EN_COURS]: "En cours",
    [V.RESOLUES]: "Résolues",
    [V.REJETEES]: "Rejetées",
    [V.TOUTES]: "Toutes",
  }),
  filtres: Object.freeze({ pharmacie: "Pharmacie", periode: "Période", motif: "Motif" }),
  colonnes: Object.freeze({
    numero: "N°",
    recueLe: "Reçue le",
    pharmacie: "Pharmacie",
    facture: "Facture",
    motif: "Motif",
    reclame: "Réclamé",
    etat: "État",
    activite: "Activité",
  }),
  nonLu: "Nouvelle activité de la pharmacie",
  aTraiter: "À traiter",
  propositionEnAttente: "Votre proposition attend la réponse de la pharmacie",
  ligneTitre: ({ facture, activite }) => [facture ? `Facture ${facture}` : null, activite ? `Dernière activité le ${activite}` : null].filter(Boolean).join(" · "),
  pied: (n) => `${n} ${pluriel(n, "réclamation", "réclamations")}`,
  vide: "Aucune réclamation reçue pour l'instant.",
  videAide: "Une pharmacie connectée qui vous adresse une réclamation sur Pharmakon apparaît ici, avec une notification.",
  videVue: "Aucune réclamation dans cette vue.",
  videFiltre: "Aucune réclamation ne correspond à ces critères.",
  erreur: "La liste des réclamations n'a pas pu être chargée.",
  interdit: "Votre rôle ne permet pas de consulter les réclamations reçues.",
});

/** ux/03 §5.8 — the document. */
export const M_RPD = Object.freeze({
  titre: (numero) => `Réclamation ${numero}`,
  identite: ({ pharmacie, statut }) => [pharmacie, statut].filter(Boolean).join(" · "),
  sections: Object.freeze({
    objet: "Objet",
    lignes: "Lignes",
    description: "Description",
    pieces: "Pièces jointes",
    echanges: "Échanges",
  }),
  onglets: Object.freeze({ detail: "Détail", echanges: (n) => `Échanges (${n})` }),
  champs: Object.freeze({
    motif: "Motif",
    recueLe: "Reçue le",
    pharmacie: "Pharmacie",
    facture: "Facture de la pharmacie",
    efacture: "Votre e-facture",
    bonCommande: "Bon de commande",
    montant: "Montant réclamé",
    montantAccorde: "Montant accordé",
  }),
  facture: ({ libelle, date }) => `${libelle ?? "—"}${date ? ` du ${date}` : ""}`,
  colonnes: Object.freeze({
    produit: "Produit",
    lot: "Lot",
    factRecue: "Fact. / Reçue",
    reclamee: "Réclamé",
    accordee: "Accordé",
    montant: "Montant",
  }),
  /** ux/03 §5.1 « Fournisseur (connected) sees » — the sentence of the state band. */
  statut: Object.freeze({
    ENVOYEE: (date) => `Reçue le ${date} — à prendre en charge.`,
    EN_COURS: (date) => `Prise en charge le ${date}.`,
    RESOLUE: (date, resolution) => `Résolue le ${date} : ${resolution}`,
    REJETEE: (date, motif) => `Rejetée le ${date} : ${motif}`,
    CLOTUREE: (date) => `Clôturée par la pharmacie le ${date}.`,
    RETIREE: (date, motif) => `Retirée par la pharmacie le ${date} : ${motif}`,
  }),
  propositionEnAttente: ({ pharmacie, montant, lignes }) =>
    `Votre proposition d'avoir${montant ? ` de ${montant} DA` : ""}${lignes ? ` (${lignes} ${pluriel(lignes, "ligne", "lignes")})` : ""} attend la réponse de ${pharmacie}.`,
  propositionRefusee: (pharmacie) => `${pharmacie} a refusé votre dernière proposition — sa réponse est dans les échanges. Vous pouvez en faire une nouvelle.`,
  deconnectee: (pharmacie) => `${pharmacie} n'est plus connectée à votre organisation : vous consultez l'historique, sans pouvoir répondre.`,
  noteCredit: (numero) => `Émettre une note de crédit sur l'e-facture ${numero}`,
  noteCreditAide: "Ouvre l'assistant « Nouvel avoir » des finances, pré-rempli avec le montant accordé.",
  aucuneLigne: "Aucune ligne : la réclamation porte sur la facture ou sur la livraison.",
  aucunePiece: "Aucune pièce jointe.",
  piece: (nom, taille) => `${nom} (${taille})`,
  pieceOuvrir: (nom) => `Ouvrir ${nom}`,
  pieceEchec: "La pièce jointe n'a pas pu être ouverte.",
  pieceCote: Object.freeze({ PHARMACIE: "jointe par la pharmacie", FOURNISSEUR: "jointe par vous" }),
  ajouterPieces: "Joindre un fichier",
  piecesAjoutees: (n) => `${n} ${pluriel(n, "pièce jointe ajoutée", "pièces jointes ajoutées")}.`,
  actions: Object.freeze({
    prendreEnCharge: "Prendre en charge",
    proposer: "Proposer un avoir…",
    accepter: "Accepter…",
    rejeter: "Rejeter…",
    retour: "Retour à la liste",
  }),
  raisons: Object.freeze({
    droits: "Votre rôle permet de consulter les réclamations, pas d'y répondre.",
    proposition: "Une proposition attend déjà la réponse de la pharmacie.",
  }),
  succes: Object.freeze({
    priseEnCharge: (numero) => `Réclamation ${numero} prise en charge.`,
    acceptee: (numero) => `Réclamation ${numero} acceptée : la pharmacie peut générer son avoir.`,
    proposee: "Proposition envoyée à la pharmacie.",
    rejetee: (numero) => `Réclamation ${numero} rejetée.`,
  }),
  chargement: "Chargement de la réclamation…",
  erreur: "La réclamation n'a pas pu être chargée.",
  introuvable: "Cette réclamation est introuvable pour votre organisation.",
  interdit: "Votre rôle ne permet pas de consulter les réclamations reçues.",
});

/** ux/03 §5.8 — « Répondre » (the timeline composer). */
export const M_RPFIL = Object.freeze({
  label: "Message à la pharmacie",
  placeholder: "Écrire à la pharmacie…",
  envoyer: "Envoyer",
  raccourci: "Ctrl+Entrée",
  envoye: "Message envoyé à la pharmacie.",
  vide: "Le message est vide.",
  long: `Le message tient en ${B.message_max} caractères au plus.`,
  ferme: "La réclamation est close : plus aucun message ne s'y ajoute.",
});

/** The thread from the supplier's side (ux/03 §5.8 « 27/09 10:40 Vous avez pris en charge »). */
export const M_RPFIL_EVENEMENTS = Object.freeze({
  CREATION: ({ pharmacie }) => `Réclamation créée par ${pharmacie}`,
  ENVOI: ({ pharmacie }) => `${pharmacie} a envoyé la réclamation.`,
  RELANCE: ({ pharmacie }) => `${pharmacie} vous a relancé.`,
  PRISE_EN_CHARGE: ({ auteur }) => `Vous avez pris en charge la réclamation${auteur ? ` (${auteur})` : ""}.`,
  MESSAGE_PHARMACIE: ({ pharmacie }) => `Message de ${pharmacie}`,
  MESSAGE_VOUS: ({ auteur }) => `Vous${auteur ? ` (${auteur})` : ""}`,
  PIECE_JOINTE: ({ n, vous, pharmacie }) => `${n} ${pluriel(n, "pièce jointe ajoutée", "pièces jointes ajoutées")} ${vous ? "par vous" : `par ${pharmacie}`}`,
  PROPOSITION: ({ montant }) => `Vous proposez un avoir${montant ? ` de ${montant} DA` : ""}.`,
  PROPOSITION_ACCEPTEE: ({ pharmacie }) => `${pharmacie} a accepté votre proposition.`,
  PROPOSITION_REFUSEE: ({ pharmacie }) => `${pharmacie} a refusé votre proposition.`,
  RESOLUTION: () => "Vous avez accepté la réclamation.",
  REJET: () => "Vous avez rejeté la réclamation.",
  AVOIR_GENERE: ({ pharmacie, numero, montant }) => `${pharmacie} a enregistré son avoir${numero ? ` ${numero}` : ""}${montant ? ` (${montant} DA)` : ""}.`,
  CLOTURE: ({ pharmacie }) => `${pharmacie} a clôturé la réclamation.`,
  RETRAIT: ({ pharmacie }) => `${pharmacie} a retiré la réclamation.`,
  AUTRE: () => "Mise à jour de la réclamation",
});

/** ux/03 §5.8 — the supplier's dialogs. */
export const M_RPDLG = Object.freeze({
  accepter: Object.freeze({
    titre: (numero, pharmacie) => `Accepter la réclamation ${numero} de ${pharmacie} ?`,
    texte: (montant) => `La pharmacie pourra générer l'avoir correspondant${montant ? ` (${montant} DA)` : ""}.`,
    message: "Message à la pharmacie (facultatif)",
    messageAide: "Repris comme résolution ; sans message, « Réclamation acceptée par le fournisseur ».",
    confirmer: "Accepter la réclamation",
    revenir: "Revenir",
  }),
  proposer: Object.freeze({
    titre: (numero) => `Proposer un avoir — ${numero}`,
    texte: "Indiquez ce que vous accordez : la pharmacie accepte votre proposition (la réclamation est alors résolue) ou la refuse en vous répondant.",
    colonnes: Object.freeze({ produit: "Produit", lot: "Lot", reclamee: "Réclamé", accordee: "Qté accordée", valeur: "Valeur (DA)" }),
    qteAccordee: (produit) => `Quantité accordée — ${produit}`,
    montant: "Montant accordé (DA)",
    montantAide: (reclame) => (reclame ? `Au plus le montant réclamé : ${reclame} DA.` : "Le montant que vous créditez à la pharmacie."),
    total: (montant) => `Total accordé : ${montant} DA`,
    totalSansPrix: "Sans prix sur ces lignes : indiquez le montant accordé.",
    message: "Message à la pharmacie",
    messageAide: "Obligatoire : expliquez votre proposition.",
    confirmer: "Envoyer la proposition",
    revenir: "Revenir",
  }),
  rejeter: Object.freeze({
    titre: (numero) => `Rejeter la réclamation ${numero} ?`,
    texte: "Le motif est transmis tel quel à la pharmacie, qui le lit en entier.",
    motif: "Motif du rejet",
    motifAide: `De ${B.motif_min} à ${B.motif_max} caractères.`,
    confirmer: "Rejeter la réclamation",
    revenir: "Revenir",
  }),
  erreurs: Object.freeze({
    quantite: (max) => `Un nombre entier de 0 à ${max}.`,
    montant: "Un montant en DA strictement positif, deux décimales au plus.",
    montantTrop: (max) => `Au plus le montant réclamé (${max} DA).`,
    rien: "Accordez au moins une quantité ou un montant.",
    message: "Écrivez un message à la pharmacie.",
    motif: `Le motif compte au moins ${B.motif_min} caractères.`,
  }),
});
